import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Music, Video as VideoIcon, UploadCloud, X, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function DropZone({ file, setFile, accept, label, testid }) {
  return (
    <label data-testid={testid}
      className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl p-6 cursor-pointer hover:border-emerald-500/50 transition-colors bg-white/[0.02]">
      <UploadCloud className="w-8 h-8 text-emerald-400" />
      <span className="text-sm text-zinc-300 font-semibold">{file ? file.name : label}</span>
      <span className="text-xs text-zinc-500">{accept}</span>
      <input type="file" accept={accept} className="hidden" onChange={(e) => setFile(e.target.files[0])} />
    </label>
  );
}

export default function UploadModal({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const [mTitle, setMTitle] = useState(""); const [mGenre, setMGenre] = useState(""); const [mBpm, setMBpm] = useState("");
  const [mDesc, setMDesc] = useState(""); const [mAudio, setMAudio] = useState(null); const [mCover, setMCover] = useState(null);

  const [vTitle, setVTitle] = useState(""); const [vTags, setVTags] = useState(""); const [vDesc, setVDesc] = useState("");
  const [vVideo, setVVideo] = useState(null); const [vThumb, setVThumb] = useState(null);

  if (open && user && !user.is_subscribed) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-[#181824] border-white/10 text-white beam-border" data-testid="upload-paywall">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> Artist Pass Required</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 text-sm">Uploading music & videos is a member feature. Join with the Artist Pass to release your work on NEXT UP.</p>
          <button data-testid="upload-paywall-cta" onClick={() => { onClose(); navigate("/subscribe"); }}
            className="mt-2 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors">
            Get Artist Pass — $25/mo
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  const submitMusic = async () => {
    if (!mTitle || !mAudio) { toast.error("Add a title and audio file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", mTitle); fd.append("genre", mGenre); fd.append("bpm", mBpm); fd.append("description", mDesc);
      fd.append("audio", mAudio); if (mCover) fd.append("cover", mCover);
      await api.post("/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Track released! 🎵");
      window.dispatchEvent(new Event("nu:refresh"));
      onClose();
      setMTitle(""); setMGenre(""); setMBpm(""); setMDesc(""); setMAudio(null); setMCover(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitVideo = async () => {
    if (!vTitle || !vVideo) { toast.error("Add a title and video file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", vTitle); fd.append("tags", vTags); fd.append("description", vDesc);
      fd.append("video", vVideo); if (vThumb) fd.append("thumbnail", vThumb);
      await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video published! 🎬");
      window.dispatchEvent(new Event("nu:refresh"));
      onClose();
      setVTitle(""); setVTags(""); setVDesc(""); setVVideo(null); setVThumb(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181824] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Upload to NEXT UP</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="music">
          <TabsList className="bg-black/40 border border-white/10">
            <TabsTrigger value="music" data-testid="tab-music" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"><Music className="w-4 h-4 mr-1" /> Music</TabsTrigger>
            <TabsTrigger value="video" data-testid="tab-video" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white"><VideoIcon className="w-4 h-4 mr-1" /> Video</TabsTrigger>
          </TabsList>

          <TabsContent value="music" className="space-y-3 mt-4">
            <DropZone file={mAudio} setFile={setMAudio} accept=".mp3,.wav,audio/*" label="Drop audio (MP3 / WAV)" testid="music-audio-drop" />
            <div className="space-y-1"><Label className="text-xs text-zinc-400">Title</Label>
              <Input data-testid="music-title" value={mTitle} onChange={(e) => setMTitle(e.target.value)} className="bg-black/40 border-white/10" placeholder="Track title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-zinc-400">Genre</Label>
                <Input data-testid="music-genre" value={mGenre} onChange={(e) => setMGenre(e.target.value)} className="bg-black/40 border-white/10" placeholder="Hip Hop" /></div>
              <div className="space-y-1"><Label className="text-xs text-zinc-400">BPM</Label>
                <Input data-testid="music-bpm" value={mBpm} onChange={(e) => setMBpm(e.target.value)} className="bg-black/40 border-white/10" placeholder="128" /></div>
            </div>
            <DropZone file={mCover} setFile={setMCover} accept="image/*" label="Cover art (optional)" testid="music-cover-drop" />
            <Textarea data-testid="music-desc" value={mDesc} onChange={(e) => setMDesc(e.target.value)} className="bg-black/40 border-white/10" placeholder="Describe your track…" />
            <button disabled={busy} onClick={submitMusic} data-testid="music-submit"
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50">
              {busy ? "Uploading…" : "Release Track"}
            </button>
          </TabsContent>

          <TabsContent value="video" className="space-y-3 mt-4">
            <DropZone file={vVideo} setFile={setVVideo} accept=".mp4,.webm,video/*" label="Drop video (MP4 / WebM)" testid="video-file-drop" />
            <div className="space-y-1"><Label className="text-xs text-zinc-400">Title</Label>
              <Input data-testid="video-title" value={vTitle} onChange={(e) => setVTitle(e.target.value)} className="bg-black/40 border-white/10" placeholder="Video title" /></div>
            <div className="space-y-1"><Label className="text-xs text-zinc-400">Tags</Label>
              <Input data-testid="video-tags" value={vTags} onChange={(e) => setVTags(e.target.value)} className="bg-black/40 border-white/10" placeholder="official, music video" /></div>
            <DropZone file={vThumb} setFile={setVThumb} accept="image/*" label="Thumbnail (optional)" testid="video-thumb-drop" />
            <Textarea data-testid="video-desc" value={vDesc} onChange={(e) => setVDesc(e.target.value)} className="bg-black/40 border-white/10" placeholder="Describe your video…" />
            <button disabled={busy} onClick={submitVideo} data-testid="video-submit"
              className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-bold transition-colors disabled:opacity-50">
              {busy ? "Uploading…" : "Publish Video"}
            </button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
